import React from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import BuoyCardPlaceholders from 'components/BuoyCardPlaceholders';
import BuoyMapMarkers from 'components/BuoyMapMarkers';
import MapBuoyCards from 'components/MapBuoyCards';
import MapControls from 'components/MapControls';
import MapHeader from 'components/MapHeader';
import useBuoysBoundingBoxQuery from 'hooks/map/useBuoysBoundingBoxQuery';
import useClustering from 'hooks/map/useClustering';
import useHydrateMapLocation from 'hooks/map/useHydrateMapLocation';
import useMap from 'hooks/map/useMap';
import useSyncMapLocationToSession from 'hooks/map/useSyncMapLocationToSession';
import useSyncMapLocationToUrl from 'hooks/map/useSyncMapLocationToUrl';
import { type NearbyStation } from 'types/buoys';
import mapLayers from 'utils/mapLayers';
import { getBuoyMapPathWithLocationObject } from 'utils/urls/mapPaths';

import styles from './BuoyMap.module.scss';

const BuoyMap: React.FC = () => {
  // Be careful changing the order of these hooks, as other hooks may depend on the location
  // set into the store via `useHydrateMapLocation`
  useHydrateMapLocation();
  useSyncMapLocationToSession();
  useSyncMapLocationToUrl(getBuoyMapPathWithLocationObject);

  const [map] = useMap();
  const { buoys, loading, error, hasBuoys, noBuoysFound } = useBuoysBoundingBoxQuery();

  const mapContainer = map?.getContainer();
  const clusters = useClustering(buoys);

  return (
    <div className={styles.slBuoyMap}>
      <MapHeader
        heading="Wave buoys"
        hasMapItems={hasBuoys || false}
        loading={loading}
        mapType="buoys"
      />
      <MapControls
        activeMap={mapLayers.BUOYS}
        className={classNames(styles.slMapLinks, styles.slMapLinksHasBuoys)}
      />
      {hasBuoys ? (
        <MapBuoyCards buoys={buoys as NearbyStation[]} />
      ) : (
        <BuoyCardPlaceholders
          numPlaceholders={12}
          loading={loading}
          error={error as Error}
          noBuoysFound={noBuoysFound}
        />
      )}

      {mapContainer &&
        createPortal(<BuoyMapMarkers clusters={clusters} buoys={buoys} />, mapContainer)}
    </div>
  );
};

export default BuoyMap;
